import { useEffect, useState } from 'react';
import { Bell, CheckCircle, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import api, { Alert } from '@/lib/api';
import SeverityBadge from '@/components/ui/SeverityBadge';


export default function Alerts() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suppressingId, setSuppressingId] = useState<string | null>(null);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    api.getAlerts().then((data) => {
      // @ts-ignore
      setAlerts(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  const handleSuppress = async (alertId: string, days: 1 | 3 | 5) => {
    setSuppressingId(alertId);
    try {
      await api.suppressAlert({ days: days, alertId: alertId }).then((data) => {
        alerts.map((alert) => {
          if (alert.id === alertId) {
            alert.isSuppressed = true;
            alert.suppressUntil = data.suppressUntil;
          }
          return alert;
        })
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSuppressingId(null);
    }
  };

  const handleUnsuppress = async (alertId: string) => {
    setSuppressingId(alertId);
    try {
      await api.unsuppressAlert(alertId).then((data) => {
        alerts.map((alert) => {
          if (alert.id === alertId) {
            alert.isSuppressed = false;
            alert.suppressUntil = data.suppressUntil;
          }
          return alert;
        })
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSuppressingId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-risk-red-soft border border-risk-red rounded-panel px-4 py-3">
        <p className="text-sm text-risk-red">Fehler beim Laden der Alerts: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Bell className="h-8 w-8 text-brand" />
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink">Alerts</h1>
          <p className="text-[12.5px] text-ink-soft">Übersicht aller Out-of-Stock Benachrichtigungen</p>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="rounded-card border border-brand-soft-border bg-brand-soft px-5 py-10 text-center">
          <CheckCircle className="h-10 w-10 text-brand mx-auto mb-3" />
          <p className="text-brand-deep font-bold">Keine aktiven Alerts</p>
          <p className="text-[12.5px] text-brand-deep/70 mt-1">Alle Produkte haben ausreichend Bestand</p>
        </div>
      ) : (
        <div className="rounded-card border border-line bg-surface overflow-hidden">
          <div className="px-5 py-4 border-b border-line">
            <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink">Aktive Alerts</h2>
            <p className="font-mono text-[11px] text-ink-soft mt-0.5">{alerts.length} Einträge</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-surface-3">
                  <th className="px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">
                    SKU / Produkt
                  </th>
                  <th className="px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">
                    Severity
                  </th>
                  <th className="px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">
                    Status
                  </th>
                  <th className="px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">
                    Letzte Benachrichtigung
                  </th>
                  <th className="px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">
                    Unterdrücken für
                  </th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert) => (
                  <tr key={alert.id} className="border-b border-surface-3 hover:bg-surface-2 transition-colors">
                    <td className="px-4 py-3">
                      <div className="text-[13.5px] font-bold text-ink">{alert.sku}</div>
                      <div className="font-mono text-[10.5px] text-ink-soft">{alert.productName}</div>
                    </td>
                    <td className="px-4 py-3">
                      <SeverityBadge severity={alert.severity} />
                    </td>
                    <td className="px-4 py-3">
                      {alert.isSuppressed ? (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-risk-yellow flex-shrink-0" />
                          <div>
                            <div className="text-[12.5px] font-bold text-risk-yellow">Unterdrückt</div>
                            <div className="font-mono text-[10.5px] text-ink-soft">
                              bis {formatDate(alert.suppressUntil)}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-risk-red flex-shrink-0" />
                          <span className="text-[12.5px] font-bold text-risk-red">Aktiv</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-[12.5px] text-ink-muted">
                      {formatDate(alert.lastSentAt)}
                    </td>
                    <td className="px-4 py-3">
                      {!alert.isSuppressed && (
                        <div className="flex gap-2">
                          {[1, 3, 5].map((days) => (
                            <button
                              key={days}
                              onClick={() => handleSuppress(alert.id, days as 1 | 3 | 5)}
                              disabled={suppressingId === alert.id}
                              className="text-xs font-bold text-brand-deep bg-brand-soft px-3.5 py-1.5 rounded-full hover:bg-brand-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {days} {days == 1 ? 'Tag' : 'Tage'}
                            </button>
                          ))}
                        </div>
                      )}
                      {alert.isSuppressed && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUnsuppress(alert.id)}
                            disabled={suppressingId === alert.id}
                            className="text-xs font-bold text-ink-muted bg-surface-2 px-3.5 py-1.5 rounded-full hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Aufheben
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
